/* eslint-disable max-lines */
import {
  useAlarmGroupDetails,
  useAlarmGroups,
  useViewLocationAlarm,
} from '@hakimo-ui/hakimo/data-access';
import {
  AlarmGroupContainer,
  InvestigationViewSkeleton,
  ListItemSkeleton,
  ListView,
} from '@hakimo-ui/hakimo/feature-shared';
import {
  AlarmGroupsDTO,
  SOPViewType,
  TimePeriod,
  UnifiedAlarmGroup,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { AlarmGroupsIcon } from '@hakimo-ui/hakimo/ui-elements';
import { FilterPanel } from '@hakimo-ui/hakimo/ui-filters';
import { PageLayout } from '@hakimo-ui/hakimo/ui-layout';
import {
  audioNotifyAtom,
  systemNotifyAtom,
  toast,
  trackFilters,
  usePrevious,
  withAuthz,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useTemporaryResolvedState } from '../hooks/useTemporaryResolvedState';
import {
  ALARM_GROUP_QUERY_INTERVAL,
  defaultFilterValues,
  filterConfigs,
} from './constants';
import FiltersHeader from './FiltersHeader';
import ListItem from './list-item/ListItem';
import { alarmGroupFiltersAtom, pageAtom, pageSizeAtom } from './state';
import { AlarmGroupFilterValues } from './types';
import { getSearchParams, notifyNewAlarmGroup } from './utils';

export function AlarmGroupsList() {
  const [page, setPage] = useAtom(pageAtom);
  const pageSize = useAtomValue(pageSizeAtom);
  const [openFilterPanel, setOpenFilterPanel] = useState(false);
  const [total, setTotal] = useState(0);
  const [alarmGroupsData, setAlarmGroupsData] = useState<UnifiedAlarmGroup[]>(
    []
  );
  const [selectedId, setSelectedId] = useState<string>();
  const [storedFilters, setStoredFilters] = useAtom(alarmGroupFiltersAtom);
  const alarmGroupFilters = useMemo(
    () => ({ ...defaultFilterValues, ...storedFilters }),
    [storedFilters]
  );
  const period = alarmGroupFilters.time?.period || TimePeriod.PAST_24_HOURS;
  const systemNotifyPreference = useAtomValue(systemNotifyAtom);
  const audioNotify = useAtomValue(audioNotifyAtom);
  const prevAlarmGroupsData = usePrevious(alarmGroupsData);

  const { addTemporaryResolvedState, getDisplayStatus } =
    useTemporaryResolvedState();

  const setAlarmGroupFilters = (filters: AlarmGroupFilterValues) => {
    setStoredFilters(filters);
  };

  const onData = (data: AlarmGroupsDTO) => {
    const newData = data.items as UnifiedAlarmGroup[];
    prevAlarmGroupsData &&
      notifyNewAlarmGroup(
        prevAlarmGroupsData,
        newData,
        page,
        systemNotifyPreference,
        audioNotify
      );
    setTotal(data.total);
    setAlarmGroupsData(newData);
  };

  const { error, isFetching } = useAlarmGroups(
    getSearchParams(page, pageSize, period, alarmGroupFilters),
    ALARM_GROUP_QUERY_INTERVAL,
    onData,
    false,
    true
  );

  useEffect(() => {
    if (error) {
      toast('Error getting alarm groups', { type: 'error' });
    }
  }, [error]);

  const { data: selectedAlarmGroup, isLoading: isLoadingDetails } =
    useAlarmGroupDetails(
      selectedId,
      undefined,
      !selectedId,
      ALARM_GROUP_QUERY_INTERVAL
    );
  const viewMutation = useViewLocationAlarm();

  const handleChangePage = (pageNum: number) => {
    setPage(pageNum);
  };

  const handleChangePeriod = (
    newPeriod: TimePeriod,
    customRange?: { start: number; end: number }
  ) => {
    if (newPeriod === TimePeriod.CUSTOM_RANGE && customRange) {
      setAlarmGroupFilters({
        ...alarmGroupFilters,
        time: {
          period: TimePeriod.CUSTOM_RANGE,
          customRangeStart: customRange.start,
          customRangeEnd: customRange.end,
        },
      });
    } else {
      setAlarmGroupFilters({
        ...alarmGroupFilters,
        time: {
          period: newPeriod,
          customRangeStart: 0,
          customRangeEnd: 0,
        },
      });
    }
    setPage(1);
  };

  const handleSelectAlarmGroup = (alarmGroup: UnifiedAlarmGroup) => {
    setSelectedId(alarmGroup.id);
    if (alarmGroup.id !== selectedId) {
      viewMutation.mutate({ alarmGroupId: alarmGroup.id, product: 'scan' });
    }
  };

  const onApplyFilters = (values: AlarmGroupFilterValues) => {
    setAlarmGroupFilters(values);
    trackFilters(values as any);
    setPage(1);
    setOpenFilterPanel(false);
  };

  useEffect(() => {
    if (!alarmGroupsData.length) {
      return;
    }
    const shouldSetSelectedItem =
      !selectedId || !alarmGroupsData.some((item) => item.id === selectedId);
    if (shouldSetSelectedItem) {
      const firstAlarmGroup = alarmGroupsData[0];
      setSelectedId(firstAlarmGroup.id);
      viewMutation.mutate({
        alarmGroupId: firstAlarmGroup.id,
        product: 'scan',
      });
    }
  }, [alarmGroupsData, selectedId, viewMutation]);

  return (
    <PageLayout variant="split" title="Alarm Groups" icon={AlarmGroupsIcon}>
      <PageLayout.List>
        <div className="flex max-h-full w-[24rem] flex-col">
          <FiltersHeader
            period={period}
            onChangePeriod={handleChangePeriod}
            onClickOpenFilterPanel={() => setOpenFilterPanel(true)}
            filterValues={alarmGroupFilters}
            isLoading={isFetching}
          />
          <div className="overflow-auto scroll-smooth px-1 pb-4 pt-2">
            {isFetching && alarmGroupsData.length === 0 ? (
              <div className="space-y-1">
                {Array.from({ length: pageSize }).map((_, i) => (
                  <ListItemSkeleton key={i} lines={3} />
                ))}
              </div>
            ) : (
              <ListView
                items={alarmGroupsData}
                total={total}
                page={page}
                pageSize={pageSize}
                onChangePage={handleChangePage}
                renderListItem={(alarmGroup) => (
                  <ListItem
                    alarmGroup={alarmGroup}
                    selected={alarmGroup.id === selectedId}
                    onClick={() => handleSelectAlarmGroup(alarmGroup)}
                    getDisplayStatus={getDisplayStatus}
                  />
                )}
              />
            )}
          </div>
        </div>
      </PageLayout.List>
      <PageLayout.Details>
        <div className="h-full">
          {selectedAlarmGroup ? (
            <AlarmGroupContainer
              alarmGroup={selectedAlarmGroup as unknown as UnifiedAlarmGroup}
              product={
                selectedAlarmGroup.product ?? UnifiedMonitoringProduct.SCAN
              }
              sopViewType={SOPViewType.STATIC}
              addTemporaryResolvedState={addTemporaryResolvedState}
              getDisplayStatus={getDisplayStatus}
            />
          ) : isLoadingDetails ||
            (isFetching && alarmGroupsData.length === 0) ? (
            <InvestigationViewSkeleton />
          ) : (
            <div className="flex h-full items-center justify-center">
              No Alarm Group found
            </div>
          )}
        </div>
      </PageLayout.Details>
      <FilterPanel
        title="Alarm Group Filters"
        open={openFilterPanel}
        filterConfigs={filterConfigs}
        initialFilterValues={alarmGroupFilters as any}
        trackingEventNameApply="apply_location_alarms_filters"
        trackingEventNameReset="reset_location_alarms_filters"
        onClose={() => setOpenFilterPanel(false)}
        onApply={onApplyFilters as any}
        enableNegativeFilters={false}
        isAlarmGroups={true}
      />
    </PageLayout>
  );
}

export default withAuthz(withErrorBoundary(AlarmGroupsList), [
  'alarm_group:view',
]);
